import React, { useEffect, useState } from "react";
import PortalOverlay from "../../../Shared/portal-overlay/portalOverlay";
import { StyledButton } from "../../../Shared/button/button";
import { testIdProps } from "../../../utils/testIdProps";
import { checkGSTIN, checkIsEmpty } from "../../../utils/validation";
import {
  INDIAN_STATES,
  parseSavedBillingAddress,
} from "../../../utils/billingAddress";
import styles from "./dialog.module.scss";

export type BillingDetails = {
  gstin: string;
  billingAddress: string;
  name: string;
};

const CITY_PATTERN = /^[A-Za-z][A-Za-z .'-]{1,48}$/;
const PIN_PATTERN = /^[1-9][0-9]{5}$/;

type Props = {
  show: boolean;
  isSaving: boolean;
  initialGstin?: string;
  initialAddress?: string;
  initialName?: string;
  onClose: () => void;
  onSubmit: (details: BillingDetails) => void;
};

function BillingDetailsDialog({
  show,
  isSaving,
  initialGstin = "",
  initialAddress = "",
  initialName = "",
  onClose,
  onSubmit,
}: Props) {
  const savedGstin = initialGstin.trim().toUpperCase();
  const askForGstin = savedGstin.length === 0;
  const [name, setName] = useState(initialName);
  const [gstin, setGstin] = useState(initialGstin);
  const [line1, setLine1] = useState("");
  const [city, setCity] = useState("");
  const [stateName, setStateName] = useState("");
  const [pinCode, setPinCode] = useState("");
  const [nameError, setNameError] = useState("");
  const [gstinError, setGstinError] = useState("");
  const [lineError, setLineError] = useState("");
  const [cityError, setCityError] = useState("");
  const [stateError, setStateError] = useState("");
  const [pinError, setPinError] = useState("");

  useEffect(() => {
    if (!show) return;
    const saved = parseSavedBillingAddress(initialAddress);
    setName(initialName);
    setGstin(initialGstin);
    setLine1(saved.line1);
    setCity(saved.city);
    setStateName(saved.stateName);
    setPinCode(saved.pinCode);
    setNameError("");
    setGstinError("");
    setLineError("");
    setCityError("");
    setStateError("");
    setPinError("");
  }, [show, initialGstin, initialAddress, initialName]);

  if (!show) return null;

  const validate = () => {
    const nextName = name.trim().replace(/\s+/g, " ");
    const nextGstin = askForGstin ? gstin.trim().toUpperCase() : savedGstin;
    const nextLine = line1.trim().replace(/\s+/g, " ");
    const nextCity = city.trim().replace(/\s+/g, " ");
    const nextState = stateName.trim();
    const nextPin = pinCode.trim();
    let nextNameError = "";
    let nextGstinError = "";
    let nextLineError = "";
    let nextCityError = "";
    let nextStateError = "";
    let nextPinError = "";

    if (checkIsEmpty(nextName)) {
      nextNameError = "Name is required";
    } else if (!/^[A-Za-z][A-Za-z .'-]{1,79}$/.test(nextName)) {
      nextNameError = "Enter a valid name";
    }

    if (askForGstin && checkIsEmpty(nextGstin)) {
      nextGstinError = "GST number is required";
    } else if (askForGstin && (!checkGSTIN(nextGstin) || nextGstin.length > 15)) {
      nextGstinError = "Enter a valid GST number";
    }

    if (checkIsEmpty(nextLine)) {
      nextLineError = "Street address is required";
    } else if (nextLine.length < 5 || !/[A-Za-z]/.test(nextLine)) {
      nextLineError = "Enter a street or building name";
    } else if (nextLine.length > 120) {
      nextLineError = "Street address must be 120 characters or fewer";
    }

    if (checkIsEmpty(nextCity)) {
      nextCityError = "City is required";
    } else if (!CITY_PATTERN.test(nextCity)) {
      nextCityError = "Enter a valid city name";
    }

    if (!INDIAN_STATES.includes(nextState)) {
      nextStateError = "Select a state";
    }

    if (!PIN_PATTERN.test(nextPin)) {
      nextPinError = "Enter a 6-digit PIN code";
    }

    setNameError(nextNameError);
    setGstinError(nextGstinError);
    setLineError(nextLineError);
    setCityError(nextCityError);
    setStateError(nextStateError);
    setPinError(nextPinError);
    if (
      nextNameError ||
      nextGstinError ||
      nextLineError ||
      nextCityError ||
      nextStateError ||
      nextPinError
    ) {
      return null;
    }
    return {
      gstin: nextGstin,
      name: nextName,
      billingAddress: `${nextLine}, ${nextCity}, ${nextState} ${nextPin}`,
    };
  };

  return (
    <PortalOverlay>
      <div
        className={styles.modalOverlay}
        role="dialog"
        aria-modal="true"
        aria-labelledby="billing-details-title"
      >
        <div className={`${styles.modalContent} ${styles.billingCard}`}>
          <div className="mb-4 border-bottom pb-3">
            <span
              id="billing-details-title"
              className="mb-0 fs-6 fw-semibold text-dark"
            >
              Billing details
            </span>
            <p className="text-muted mb-0 mt-2" style={{ fontSize: "13px", lineHeight: 1.5 }}>
              {askForGstin
                ? "Add the GST number and billing address, then continue to payment."
                : "Check the billing address, then continue to payment."}
            </p>
          </div>

          <div className="mb-3">
            <label className={styles.fieldLabel} htmlFor="new-billing-name">
              <span>
                Name
                <span className={styles.requiredMark}>*</span>
              </span>
            </label>
            <input
              {...testIdProps("new-billing-name")}
              id="new-billing-name"
              type="text"
              className="form-control bg-light border-0"
              placeholder="Organisation name"
              value={name}
              maxLength={80}
              onChange={(event) => {
                setName(event.target.value);
                setNameError("");
              }}
              style={{ fontSize: "14px", height: "48px" }}
            />
            {nameError && (
              <div className="text-danger mt-1" style={{ fontSize: "12px" }}>
                {nameError}
              </div>
            )}
          </div>

          {askForGstin && (
          <div className="mb-3">
            <label className={styles.fieldLabel} htmlFor="new-billing-gst-number">
              <span>
                GST number
                <span className={styles.requiredMark}>*</span>
              </span>
            </label>
            <input
              {...testIdProps("new-billing-gst-number")}
              type="text"
              className="form-control bg-light border-0"
              placeholder="e.g. 29ABCDE1234F1Z5"
              value={gstin}
              maxLength={15}
              onChange={(event) => {
                setGstin(event.target.value.toUpperCase());
                setGstinError("");
              }}
              style={{ fontSize: "14px", height: "48px", letterSpacing: "0.04em" }}
            />
            <div className={styles.hint}>15-character GSTIN, for example 29ABCDE1234F1Z5</div>
            {gstinError && (
              <div className="text-danger mt-1" style={{ fontSize: "12px" }}>
                {gstinError}
              </div>
            )}
          </div>
          )}

          <div className="mb-3">
            <label className={styles.fieldLabel} htmlFor="new-billing-address-line">
              <span>
                Street address
                <span className={styles.requiredMark}>*</span>
              </span>
            </label>
            <input
              {...testIdProps("new-billing-address-line")}
              id="new-billing-address-line"
              type="text"
              className="form-control bg-light border-0"
              placeholder="221B Baker Street"
              value={line1}
              maxLength={120}
              onChange={(event) => {
                setLine1(event.target.value);
                setLineError("");
              }}
              style={{ fontSize: "14px", height: "48px" }}
            />
            {lineError && (
              <div className="text-danger mt-1" style={{ fontSize: "12px" }}>
                {lineError}
              </div>
            )}
          </div>

          <div className="d-flex gap-3 mb-3">
            <div className="flex-fill">
              <label className={styles.fieldLabel} htmlFor="new-billing-city">
                <span>
                  City
                  <span className={styles.requiredMark}>*</span>
                </span>
              </label>
              <input
                {...testIdProps("new-billing-city")}
                id="new-billing-city"
                type="text"
                className="form-control bg-light border-0"
                placeholder="Pune"
                value={city}
                maxLength={50}
                onChange={(event) => {
                  setCity(event.target.value);
                  setCityError("");
                }}
                style={{ fontSize: "14px", height: "48px" }}
              />
              {cityError && (
                <div className="text-danger mt-1" style={{ fontSize: "12px" }}>
                  {cityError}
                </div>
              )}
            </div>
            <div style={{ width: "140px" }}>
              <label className={styles.fieldLabel} htmlFor="new-billing-pin">
                <span>
                  PIN code
                  <span className={styles.requiredMark}>*</span>
                </span>
              </label>
              <input
                {...testIdProps("new-billing-pin")}
                id="new-billing-pin"
                type="text"
                inputMode="numeric"
                className="form-control bg-light border-0"
                placeholder="411001"
                value={pinCode}
                maxLength={6}
                onChange={(event) => {
                  setPinCode(event.target.value.replace(/\D/g, "").slice(0, 6));
                  setPinError("");
                }}
                style={{ fontSize: "14px", height: "48px" }}
              />
              {pinError && (
                <div className="text-danger mt-1" style={{ fontSize: "12px" }}>
                  {pinError}
                </div>
              )}
            </div>
          </div>

          <div className="mb-4">
            <label className={styles.fieldLabel} htmlFor="new-billing-state">
              <span>
                State
                <span className={styles.requiredMark}>*</span>
              </span>
            </label>
            <select
              {...testIdProps("new-billing-state")}
              id="new-billing-state"
              className="form-control bg-light border-0"
              value={stateName}
              onChange={(event) => {
                setStateName(event.target.value);
                setStateError("");
              }}
              style={{ fontSize: "14px", height: "48px" }}
            >
              <option value="">Select state</option>
              {INDIAN_STATES.map((name) => (
                <option key={name} value={name}>
                  {name}
                </option>
              ))}
            </select>
            {stateError && (
              <div className="text-danger mt-1" style={{ fontSize: "12px" }}>
                {stateError}
              </div>
            )}
          </div>

          <div className="d-flex gap-3">
            <StyledButton
              id="new-billing-details-cancel"
              style={{
                flex: "1",
                backgroundColor: "transparent",
                color: "#6B7280",
                border: "0px",
                padding: "12px 24px",
                fontSize: "14px",
                fontWeight: "500",
                textDecoration: "underline",
              }}
              hoverStyle={{
                flex: "1",
                backgroundColor: "transparent",
                color: "#6B7280",
                border: "0px",
                padding: "12px 24px",
                fontSize: "14px",
                fontWeight: "500",
                textDecoration: "underline",
              }}
              onClick={onClose}
              disabled={isSaving}
            >
              Cancel
            </StyledButton>
            <StyledButton
              id="new-billing-details-continue"
              style={{
                flex: "1",
                backgroundColor: "#000000",
                color: "#FFFFFF",
                borderRadius: "8px",
                padding: "12px 24px",
                fontSize: "14px",
                fontWeight: "500",
                border: "none",
              }}
              hoverStyle={{
                flex: "1",
                backgroundColor: "#000000",
                color: "#FFFFFF",
                borderRadius: "8px",
                padding: "12px 24px",
                fontSize: "14px",
                fontWeight: "500",
                border: "none",
              }}
              isLoading={isSaving}
              disabled={isSaving}
              onClick={() => {
                const details = validate();
                if (details) onSubmit(details);
              }}
            >
              Continue to payment
            </StyledButton>
          </div>
        </div>
      </div>
    </PortalOverlay>
  );
}

export default BillingDetailsDialog;
